import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BarChart3, Database, Download, FileSpreadsheet, HardDrive, LayoutDashboard, List, Menu, RefreshCw, Search, Upload, X } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const BRAND = {
  primary: 'bg-[#74c476]',
  primaryHover: 'hover:bg-[#5aa85c]',
  secondary: 'bg-[#222222]',
  accent: 'text-[#74c476]',
  light: 'bg-stone-50'
};

const authFetchLocal = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm">
    <div className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2">{label}</div>
    <div className="text-2xl font-bold text-stone-900">{value}</div>
    {hint ? <div className="text-sm text-stone-500 mt-1">{hint}</div> : null}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm h-[360px]">
    <h3 className="text-sm font-bold mb-3">{title}</h3>
    <div className="h-[300px]">{children}</div>
  </div>
);

export default function SpanishParliamentActivityWorkspace({ user, onLogout, onBackToHub, apiUrl }) {
  const [view, setView] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ functions: [], specificFunctions: [], specificByFunction: {}, legislatures: [] });
  const [search, setSearch] = useState('');
  const [functionName, setFunctionName] = useState('');
  const [legislature, setLegislature] = useState('');
  const [selectedFunctionForChart, setSelectedFunctionForChart] = useState('');
  const [selectedSpecificForChart, setSelectedSpecificForChart] = useState('');
  const [evolutionFunctionRows, setEvolutionFunctionRows] = useState([]);
  const [evolutionSpecificRows, setEvolutionSpecificRows] = useState([]);
  const [importMsg, setImportMsg] = useState(null);

  const canImport = user && user.role === 'admin';
  const displayUserName = [user?.name, user?.surname].filter(Boolean).join(' ') || user?.fullName || user?.username || 'User';
  const formatLegislatureLabel = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '—';
    if (/^constituyente legislatura$/i.test(raw)) return 'Legislatura Constituyente';
    return raw;
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliament-activity/dashboard`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load dashboard');
      setDashboard(data);
    } catch (error) {
      console.error(error);
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliament-activity/filters`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load filters');
      setFilters({
        functions: Array.isArray(data?.functions) ? data.functions : [],
        specificFunctions: Array.isArray(data?.specificFunctions) ? data.specificFunctions : [],
        specificByFunction: data?.specificByFunction && typeof data.specificByFunction === 'object' ? data.specificByFunction : {},
        legislatures: Array.isArray(data?.legislatures) ? data.legislatures : []
      });
    } catch (error) {
      console.error(error);
      setFilters({ functions: [], specificFunctions: [], specificByFunction: {}, legislatures: [] });
    }
  }, [apiUrl]);

  const fetchEvolution = useCallback(async ({ functionLabel = '', specificLabel = '', setter }) => {
    try {
      const params = new URLSearchParams();
      if (functionLabel) params.set('functionName', functionLabel);
      if (specificLabel) params.set('specificFunction', specificLabel);
      const query = params.toString();
      const res = await authFetchLocal(`${apiUrl}/spanish-parliament-activity/evolution${query ? `?${query}` : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load evolution');
      setter(Array.isArray(data?.rows) ? data.rows : []);
    } catch (error) {
      console.error(error);
      setter([]);
    }
  }, [apiUrl]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (functionName) params.set('functionName', functionName);
      if (legislature) params.set('legislature', legislature);
      const query = params.toString();
      const res = await authFetchLocal(`${apiUrl}/spanish-parliament-activity/rows${query ? `?${query}` : ''}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load rows');
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, functionName, legislature, search]);

  useEffect(() => {
    fetchDashboard();
    fetchFilters();
  }, [fetchDashboard, fetchFilters]);

  useEffect(() => {
    if (view === 'rows') fetchRows();
  }, [fetchRows, view]);

  useEffect(() => {
    if (!selectedFunctionForChart && filters.functions.length > 0) {
      setSelectedFunctionForChart(filters.functions[0]);
    }
  }, [filters.functions, selectedFunctionForChart]);

  useEffect(() => {
    const availableSpecific = selectedFunctionForChart
      ? (filters.specificByFunction?.[selectedFunctionForChart] || [])
      : filters.specificFunctions;

    if (availableSpecific.length === 0) {
      setSelectedSpecificForChart('');
      return;
    }

    if (!selectedSpecificForChart || !availableSpecific.includes(selectedSpecificForChart)) {
      setSelectedSpecificForChart(availableSpecific[0]);
    }
  }, [filters.specificByFunction, filters.specificFunctions, selectedFunctionForChart, selectedSpecificForChart]);

  useEffect(() => {
    if (!selectedFunctionForChart) {
      setEvolutionFunctionRows([]);
      return;
    }
    fetchEvolution({ functionLabel: selectedFunctionForChart, setter: setEvolutionFunctionRows });
  }, [fetchEvolution, selectedFunctionForChart]);

  useEffect(() => {
    if (!selectedSpecificForChart) {
      setEvolutionSpecificRows([]);
      return;
    }
    fetchEvolution({ specificLabel: selectedSpecificForChart, setter: setEvolutionSpecificRows });
  }, [fetchEvolution, selectedSpecificForChart]);

  const chartRows = useMemo(
    () => (dashboard?.evolutionByLegislature || []).map((row) => ({ label: row.legislature, count: row.totalActivities })),
    [dashboard]
  );

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportMsg(null);
    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const res = await authFetchLocal(`${apiUrl}/spanish-parliament-activity/import-xlsx`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Import failed');

      setImportMsg({
        type: 'success',
        text: `Imported rows: ${Number(data?.summary?.totalRows || 0)} | Total activities: ${Number(data?.summary?.totalActivities || 0)}`
      });

      await Promise.all([fetchDashboard(), fetchFilters()]);
      if (view === 'rows') await fetchRows();
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Import failed' });
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const downloadBlobFromEndpoint = async (endpoint, filename) => {
    const res = await authFetchLocal(`${apiUrl}${endpoint}`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data?.error || 'Export failed');
    }
    const contentDisposition = res.headers.get('content-disposition') || '';
    const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    let resolvedFilename = filename;
    if (filenameMatch?.[1]) {
      try {
        resolvedFilename = decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
      } catch {
        resolvedFilename = filenameMatch[1].replace(/"/g, '').trim() || filename;
      }
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = resolvedFilename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    try {
      await downloadBlobFromEndpoint(
        '/spanish-parliament-activity/export-csv',
        `spanish_parliament_activity_${new Date().toISOString().split('T')[0]}.csv`
      );
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportXlsx = async () => {
    try {
      await downloadBlobFromEndpoint(
        '/spanish-parliament-activity/export-xlsx',
        `spanish_parliament_activity_${new Date().toISOString().split('T')[0]}.xlsx`
      );
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  const handleExportOriginal = async () => {
    try {
      await downloadBlobFromEndpoint(
        '/spanish-parliament-activity/export-original',
        `spanish_parliament_activity_original_${new Date().toISOString().split('T')[0]}.sql`
      );
    } catch (error) {
      setImportMsg({ type: 'error', text: error.message || 'Export failed' });
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-screen font-sans text-stone-800 bg-stone-50 overflow-hidden relative">
      <div className="md:hidden bg-[#222222] text-white p-4 flex justify-between items-center shadow-md z-50 shrink-0">
        <div className="flex flex-col">
          <h1 className="text-lg font-bold tracking-tight">Spanish Parliament Activity</h1>
          <p className="text-xs text-stone-300">Data workspace</p>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-1">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 w-64 ${BRAND.secondary} text-stone-100 flex flex-col shadow-xl z-40 transform transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:relative md:translate-x-0
        `}
      >
        <div className="p-6 border-b border-stone-700 hidden md:block">
          <h1 className="text-xl font-sans font-bold tracking-tight">Spanish Parliament</h1>
          <p className="text-xs text-stone-400 mt-1 uppercase tracking-widest">Activity Database</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <div className="px-4 py-2 mb-4">
            <div className="text-xs text-stone-400 uppercase font-bold">Logged in as</div>
            <div className="font-medium text-white truncate">{displayUserName}</div>
            <div className="flex justify-between items-center text-xs mt-1">
              <span className="text-green-400 capitalize bg-stone-800 px-1 rounded">{user?.role}</span>
              <button onClick={onLogout} title="Log Out">
                <ArrowLeft size={14} className="text-stone-400 hover:text-red-400" />
              </button>
            </div>
          </div>

          <button
            onClick={() => { setView('dashboard'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'dashboard' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => { setView('rows'); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'rows' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
          >
            <List size={18} />
            <span>Rows</span>
          </button>

          <div className="pt-4 mt-4 border-t border-stone-700 space-y-1">
            {user?.role !== 'viewer' && <button
              onClick={() => { setView('data'); setMobileMenuOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium ${view === 'data' ? `${BRAND.primary} text-white shadow-lg` : 'text-stone-300 hover:bg-stone-700'}`}
            >
              <HardDrive size={18} />
              <span>Data Management</span>
            </button>}
            <button
              onClick={() => {
                onBackToHub();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm transition-all text-sm font-medium text-stone-300 hover:bg-stone-700"
            >
              <Database size={18} />
              <span>Databases Hub</span>
            </button>
          </div>
        </nav>
      </aside>

      <main className={`flex-1 overflow-auto ${BRAND.light} relative p-5 md:p-8`}>
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 border-b border-stone-300 pb-4 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Spanish Parliament Activity</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  fetchDashboard();
                  fetchFilters();
                  if (view === 'rows') fetchRows();
                }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-stone-300 bg-white text-stone-700 hover:bg-stone-100 text-sm"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {importMsg && (
            <div className={`mb-4 p-3 rounded-sm text-sm ${importMsg.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}>
              {importMsg.text}
            </div>
          )}

          {view === 'dashboard' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                <StatCard label="Total Activities" value={Number(dashboard?.summary?.totalActivities || 0).toLocaleString()} />
                <StatCard label="Functions" value={Number(dashboard?.summary?.distinctFunctions || 0).toLocaleString()} />
                <StatCard label="Specific Functions" value={Number(dashboard?.summary?.distinctSpecificFunctions || 0).toLocaleString()} />
                <StatCard label="Legislatures" value={Number(dashboard?.summary?.distinctLegislatures || 0).toLocaleString()} />
                <StatCard label="Data Range" value={`${formatLegislatureLabel(dashboard?.summary?.minLegislature)} - ${formatLegislatureLabel(dashboard?.summary?.maxLegislature)}`} />
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
              <ChartCard title="Activity by Legislature (Constituent to XV)">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRows} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" angle={-30} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#74c476" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Function Evolution (selectable)">
                <div className="mb-2">
                  <select
                    value={selectedFunctionForChart}
                    onChange={(e) => setSelectedFunctionForChart(e.target.value)}
                    className="w-full border border-stone-300 p-2 text-sm bg-white"
                  >
                    {(filters.functions || []).map((item) => (
                      <option key={`fn-chart-${item}`} value={item}>{item}</option>
                    ))}
                  </select>
                </div>
                <div className="h-[258px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={evolutionFunctionRows} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="legislature" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} interval={0} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="activityCount" stroke="#264653" strokeWidth={2.5} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
              </div>

              <div className="grid grid-cols-1 gap-4 mb-6">
                <ChartCard title="Specific Activity Evolution (selectable)">
                  <div className="mb-2">
                    <select
                      value={selectedSpecificForChart}
                      onChange={(e) => setSelectedSpecificForChart(e.target.value)}
                      className="w-full border border-stone-300 p-2 text-sm bg-white"
                    >
                      {(
                        selectedFunctionForChart
                          ? (filters.specificByFunction?.[selectedFunctionForChart] || [])
                          : (filters.specificFunctions || [])
                      ).map((item) => (
                        <option key={`specific-chart-${item}`} value={item}>{item}</option>
                      ))}
                    </select>
                  </div>
                  <div className="h-[258px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={evolutionSpecificRows} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="legislature" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} interval={0} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="activityCount" stroke="#8D6A9F" strokeWidth={2.5} dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </ChartCard>
              </div>

              <div className="mt-6 bg-white border border-stone-200 rounded-sm shadow-sm">
                <div className="p-4 border-b border-stone-200 flex items-center gap-2">
                  <BarChart3 size={16} className="text-stone-500" />
                  <h3 className="text-sm font-bold text-stone-800 uppercase">Top Functions</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {(dashboard?.byFunction || []).slice(0, 12).map((item) => (
                    <div key={item.label} className="px-4 py-2 text-sm flex items-center justify-between">
                      <span className="text-stone-700">{item.label}</span>
                      <span className="font-bold text-stone-900">{Number(item.count || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {view === 'rows' && (
            <>
              <div className="bg-white border border-stone-200 p-4 rounded-sm shadow-sm mb-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <label className="block md:col-span-2">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Search</span>
                    <div className="relative">
                      <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Function, specific function, legislature"
                        className="w-full border border-stone-300 pl-8 pr-2 py-2 text-sm"
                      />
                    </div>
                  </label>

                  <label className="block">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Function</span>
                    <select value={functionName} onChange={(e) => setFunctionName(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.functions.map((item) => (
                        <option key={item} value={item}>{item}</option>
                      ))}
                    </select>
                  </label>

                  <label className="block">
                    <span className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">Legislature</span>
                    <select value={legislature} onChange={(e) => setLegislature(e.target.value)} className="w-full border border-stone-300 p-2 text-sm bg-white">
                      <option value="">All</option>
                      {filters.legislatures.map((item) => (
                        <option key={item} value={item}>{item}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={fetchRows}
                    className={`px-3 py-2 text-sm text-white rounded-sm ${BRAND.primary} ${BRAND.primaryHover}`}
                  >
                    Apply filters
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setFunctionName('');
                      setLegislature('');
                    }}
                    className="px-3 py-2 text-sm rounded-sm border border-stone-300 bg-white hover:bg-stone-100"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="bg-white border border-stone-200 rounded-sm shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-stone-200 text-sm text-stone-600">
                  Total rows: <span className="font-bold text-stone-900">{rows.length.toLocaleString()}</span>
                  {loading ? <span className="ml-2 text-stone-400">Loading...</span> : null}
                </div>
                <div className="overflow-auto max-h-[70vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-500 uppercase text-xs tracking-wide">
                      <tr>
                        <th className="text-left p-3 border-b border-stone-200">Function</th>
                        <th className="text-left p-3 border-b border-stone-200">Specific Function</th>
                        <th className="text-left p-3 border-b border-stone-200">Legislature</th>
                        <th className="text-right p-3 border-b border-stone-200">Activity Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {rows.map((row) => (
                        <tr key={row.id} className="hover:bg-stone-50">
                          <td className="p-3 align-top">{row.functionName}</td>
                          <td className="p-3 align-top">{row.specificFunction}</td>
                          <td className="p-3 align-top whitespace-nowrap">{row.legislature}</td>
                          <td className="p-3 align-top text-right font-bold">{Number(row.activityCount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-stone-400">No rows found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {view === 'data' && user?.role !== 'viewer' && (
            <div className="space-y-6">
              <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between border-b border-stone-300 pb-4">
                <h2 className="text-3xl font-bold text-stone-900">Data Management</h2>
                <button onClick={fetchDashboard} className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 bg-white text-sm hover:bg-stone-50">
                  <RefreshCw size={14} /> Refresh status
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {canImport && (
                <div className="bg-white p-8 rounded-sm shadow-sm border border-stone-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-green-100 text-green-800 rounded-full">
                      <Upload size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-stone-800">Import XLSX</h3>
                      <p className="text-sm text-stone-500">Upload and replace the active parliament activity workbook.</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-stone-300 rounded-lg p-8 text-center hover:bg-stone-50 transition-colors relative">
                      <input type="file" accept=".xlsx" onChange={handleImport} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      <div className="text-stone-400 flex flex-col items-center">
                        {loading ? <RefreshCw className="animate-spin mb-2" /> : <Upload className="mb-2" size={32} />}
                        <span className="font-medium text-stone-600">Click to upload XLSX file</span>
                        <span className="text-xs mt-1">Expected source: Actividades_Parlamento_1977_2025.xlsx</span>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                <div className={`bg-white p-8 rounded-sm shadow-sm border border-stone-200 ${canImport ? '' : 'lg:col-span-2'}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-stone-100 text-stone-800 rounded-full">
                      <Database size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-stone-800">Export / Source</h3>
                      <p className="text-sm text-stone-500">Download data in CSV, Excel or original workbook format.</p>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm text-stone-700">
                    <button onClick={handleExportCsv} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="text-green-600" size={20} />
                        <div className="text-left">
                          <div className="font-bold text-stone-800 group-hover:text-green-800">Export full CSV</div>
                          <div className="text-xs text-stone-500">All parliament activity rows (normalized)</div>
                        </div>
                      </div>
                      <Download size={18} />
                    </button>

                    <button onClick={handleExportXlsx} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                      <div className="flex items-center gap-3">
                        <FileSpreadsheet className="text-green-600" size={20} />
                        <div className="text-left">
                          <div className="font-bold text-stone-800 group-hover:text-green-800">Export full Excel (.xlsx)</div>
                          <div className="text-xs text-stone-500">Workbook with all current rows</div>
                        </div>
                      </div>
                      <Download size={18} />
                    </button>

                    <button onClick={handleExportOriginal} className="flex items-center justify-between w-full p-4 border border-stone-300 rounded-sm hover:border-green-600 hover:bg-green-50 hover:text-green-800 transition-all group">
                      <div className="flex items-center gap-3">
                        <HardDrive className="text-green-600" size={20} />
                        <div className="text-left">
                          <div className="font-bold text-stone-800 group-hover:text-green-800">Download full DB in original format</div>
                          <div className="text-xs text-stone-500">Current storage engine snapshot (.db SQLite / .sql PostgreSQL)</div>
                        </div>
                      </div>
                      <Download size={18} />
                    </button>

                    <div>
                      <div className="text-xs font-bold uppercase text-stone-400 mb-1">Storage engine</div>
                      <div className="capitalize">{dashboard?.storageEngine || 'sqlite'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
